/**
 * The cave settings as editable fields (docs/spec/07 §4): numbers with limits and steps, the three
 * colours, and the random fill. Every change returns a new doc (or the same one). Pure.
 */

import type { CaveColors, CaveFill } from '../../caves/types';
import type { KitDoc } from './doc';
import { MAX_FILL_ITEMS } from './doc';
import { MAX_H, MAX_W, MIN_H, MIN_W, resizeGrid } from './grid';
import { canFill } from './palette';

export type FieldId =
  | 'width' | 'height' | 'diamonds' | 'time' | 'tick' | 'seed'
  | 'diamondValue' | 'extraValue' | 'amoebaTime' | 'magicWallTime';

export interface NumberField {
  readonly id: FieldId;
  /** Up to six characters. */
  readonly label: string;
  readonly tip: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /** The step with Shift held, or once a held button speeds up. */
  readonly big: number;
  get(d: KitDoc): number;
  set(d: KitDoc, v: number): KitDoc;
}

type Spec = Omit<NumberField, 'id' | 'get' | 'set'>;

function spec(label: string, min: number, max: number, step: number, big: number, tip: string): Spec {
  return { label, min, max, step, big, tip };
}

function plain(id: Exclude<FieldId, 'width' | 'height'>, s: Spec): NumberField {
  return { id, ...s, get: (d) => d[id], set: (d, v) => ({ ...d, [id]: v }) };
}

export const NUMBER_FIELDS: Readonly<Record<FieldId, NumberField>> = {
  width: {
    id: 'width',
    ...spec('WIDTH', MIN_W, MAX_W, 1, 5, 'CAVE WIDTH, 12-80 CELLS: THE STEEL BORDER MOVES WITH IT'),
    get: (d) => d.grid.w,
    set: (d, v) => ({ ...d, grid: resizeGrid(d.grid, v, d.grid.h) }),
  },
  height: {
    id: 'height',
    ...spec('HEIGHT', MIN_H, MAX_H, 1, 5, 'CAVE HEIGHT, 8-50 CELLS: THE STEEL BORDER MOVES WITH IT'),
    get: (d) => d.grid.h,
    set: (d, v) => ({ ...d, grid: resizeGrid(d.grid, d.grid.w, v) }),
  },
  diamonds: plain('diamonds', spec('QUOTA', 1, 999, 1, 10, 'DIAMONDS NEEDED TO OPEN THE EXIT')),
  time: plain('time', spec('TIME', 1, 999, 1, 10, 'SECONDS ON THE CAVE CLOCK')),
  tick: plain('tick', spec('TICK', 60, 400, 5, 20, 'MILLISECONDS PER SCAN, 60-400: LOWER IS FASTER')),
  seed: plain('seed', spec('SEED', 0, 99999, 1, 100, 'SEEDS THE RANDOM FILL: A NEW SEED, A NEW LAYOUT (CLICK: ROLL)')),
  diamondValue: plain('diamondValue', spec('VALUE', 0, 999, 1, 5, 'POINTS FOR EACH DIAMOND UNTIL THE QUOTA')),
  extraValue: plain('extraValue', spec('EXTRA', 0, 999, 1, 5, 'POINTS FOR EACH DIAMOND AFTER THE QUOTA')),
  amoebaTime: plain('amoebaTime', spec('AMOEBA', 0, 999, 1, 10, 'SECONDS THE AMOEBA GROWS SLOWLY BEFORE IT SPEEDS UP')),
  magicWallTime: plain('magicWallTime', spec('MAGIC', 1, 999, 1, 10, 'SECONDS A MAGIC WALL RUNS ONCE STARTED')),
};

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Change a number field by `delta`, kept within its limits. */
export function nudge(d: KitDoc, id: FieldId, delta: number): KitDoc {
  const f = NUMBER_FIELDS[id];
  const old = f.get(d);
  const v = clamp(Math.round(old + delta), f.min, f.max);
  return v === old ? d : f.set(d, v);
}

export function setField(d: KitDoc, id: FieldId, value: number): KitDoc {
  const f = NUMBER_FIELDS[id];
  const v = clamp(Math.round(value), f.min, f.max);
  return v === f.get(d) ? d : f.set(d, v);
}

// ---- colours ----------------------------------------------------------------------------------

export type ColourSlot = 0 | 1 | 2;

export const COLOUR_SLOTS: readonly { readonly label: string; readonly tip: string }[] = [
  { label: 'EARTH', tip: 'EARTH COLOUR: DIRT AND BRICK' },
  { label: 'ROCK', tip: 'ROCK COLOUR: BOULDERS AND STEEL' },
  { label: 'BRIGHT', tip: 'BRIGHT COLOUR: DIAMONDS, DIGBY, MORTAR (1-7, AS ON THE C64)' },
];

/** Colours a slot may take: never black, and the bright colour is 1-7 as in C64 multicolour mode. */
export function colourChoices(slot: ColourSlot): number[] {
  return Array.from({ length: slot === 2 ? 7 : 15 }, (_, i) => i + 1);
}

/** The next (or previous) colour for a slot, skipping the other two slots' colours. */
export function cycleColour(d: KitDoc, slot: ColourSlot, dir: 1 | -1): KitDoc {
  const choices = colourChoices(slot).filter((c) => !d.colors.some((o, i) => i !== slot && o === c));
  if (choices.length === 0) return d;
  const at = choices.indexOf(d.colors[slot]);
  const n = choices.length;
  const next = choices[at < 0 ? (dir > 0 ? 0 : n - 1) : (at + dir + n) % n];
  if (next === undefined || next === d.colors[slot]) return d;
  const colors = [...d.colors] as [number, number, number];
  colors[slot] = next;
  return { ...d, colors: colors as CaveColors };
}

// ---- random fill -------------------------------------------------------------------------------

export function fillTotal(fill: CaveFill): number {
  return fill.items.reduce((n, [, p]) => n + p, 0);
}

function withItems(d: KitDoc, items: readonly (readonly [string, number])[]): KitDoc {
  return { ...d, fill: { base: d.fill.base, items } };
}

/** What `?` becomes when no item is rolled. */
export function setFillBase(d: KitDoc, ch: string): KitDoc {
  return canFill(ch) && ch !== d.fill.base ? { ...d, fill: { base: ch, items: d.fill.items } } : d;
}

/** Set item `i` to `ch`; `i` one past the end adds an item (5 %, or what is left). */
export function setFillItem(d: KitDoc, i: number, ch: string): KitDoc {
  if (!canFill(ch)) return d;
  const items = [...d.fill.items];
  const old = items[i];
  if (old) {
    if (old[0] === ch) return d;
    items[i] = [ch, old[1]];
  } else {
    if (i !== items.length || i >= MAX_FILL_ITEMS) return d;
    items.push([ch, Math.min(5, Math.max(0, 100 - fillTotal(d.fill)))]);
  }
  return withItems(d, items);
}

export function removeFillItem(d: KitDoc, i: number): KitDoc {
  if (i < 0 || i >= d.fill.items.length) return d;
  return withItems(d, d.fill.items.filter((_, k) => k !== i));
}

/** Change item `i`'s percentage, keeping the items' total at most 100. */
export function nudgeFillPercent(d: KitDoc, i: number, delta: number): KitDoc {
  const item = d.fill.items[i];
  if (!item) return d;
  const others = fillTotal(d.fill) - item[1];
  const p = clamp(Math.round((item[1] + delta) * 10) / 10, 0, Math.max(0, 100 - others));
  if (p === item[1]) return d;
  return withItems(d, d.fill.items.map((it, k): readonly [string, number] => (k === i ? [it[0], p] : it)));
}
